import { Edge, Node, Position } from '@xyflow/react';
import {
	NODE_HEIGHT,
	NODE_WIDTH,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/Nodes';
import dagre from '@dagrejs/dagre';
import {
	EdgeRes,
	EdgeTypes,
	NodeData,
	NodeRes,
	NodeTypes,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/chart.model.ts';
import {
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { formatCapValue } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableUtils.ts';
import { LegalFormType } from '@dealstack/shared/lib/generated';

type ChartXPosition = {
	chartMinX: number;
	chartMaxX: number;
};

export type CapChartLocalStorageSettings = {
	valueDisplayMode: CAP_TABLE_VALUE_DISPLAY_MODE;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	showSummarized: boolean;
};

export const getLayoutedElements = (
	nodes: NodeRes[],
	edges: EdgeRes[],
	dagreGraph: dagre.graphlib.Graph,
	chartSettings: CapChartLocalStorageSettings
): { nodes: Node<NodeData>[]; edges: Edge<EdgeRes>[] } => {
	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.fromNodeId, edge.toNodeId);
	});

	dagre.layout(dagreGraph);

	const newNodes = nodes.map((node) => {
		const loanEdges = edges.filter((e) => e.type === 'loan');
		return makeNode(node, dagreGraph, loanEdges);
	});

	const newEdges = edges.map((e) => makeEdge(e, newNodes, chartSettings));

	return { nodes: newNodes, edges: newEdges };
};

const makeNode = (
	node: NodeRes,
	dagreGraph: dagre.graphlib.Graph,
	loanEdges: EdgeRes[]
): Node<NodeData> => {
	const nodeWithPosition = dagreGraph.node(node.id);
	let loanSourceHandlePosition: Position | null = null;

	const sourceLoanEdges = loanEdges.filter((edge) => edge.fromNodeId === node.id);
	const targetLoanEdges = loanEdges.filter((edge) => edge.toNodeId === node.id);
	const hasSourceLoan = !!sourceLoanEdges?.length;
	const hasTargetLoan = !!targetLoanEdges?.length;

	if (hasSourceLoan) {
		loanSourceHandlePosition = isDagreNodeEndAfterCenter(nodeWithPosition, dagreGraph)
			? Position.Right
			: Position.Left;
	}
	return {
		...node,
		id: node.id,
		type: getLegalType(node.legalFormType),
		data: {
			label: node.name,
			registeredName: node.name,
			registeredId: node.registrationNumber,
			countryCode: node.jurisdiction,
			isMipGroup: node.isMipGroup,
			loanData: {
				source: hasSourceLoan,
				target: hasTargetLoan,
				sourcePosition: loanSourceHandlePosition,
			},
		},
		position: {
			x: (nodeWithPosition?.x || 0) - (nodeWithPosition.width || 0) / 2,
			y: (nodeWithPosition?.y || 0) - (nodeWithPosition.height || 0) / 2,
		},
	};
};

export const getNewEdgePosition = (
	existingNode: Node<NodeData>,
	chartMinX: number,
	chartMaxX: number,
	loanEdges: Edge[]
) => {
	let loanSourceHandlePosition: Position | null = null;
	const sourceLoanEdges = loanEdges.filter((edge) => edge.source === existingNode.id);
	const hasSourceLoan = !!sourceLoanEdges?.length;

	if (!existingNode?.data?.loanData || !hasSourceLoan) {
		return null;
	}

	if (hasSourceLoan) {
		const nodeX = existingNode.position.x;
		const nodeAfterCenter = isNodeEndAfterCenter(
			nodeX,
			{
				chartMinX,
				chartMaxX,
			},
			existingNode.width
		);
		loanSourceHandlePosition = nodeAfterCenter ? Position.Right : Position.Left;
	}

	if (existingNode.data.loanData.sourcePosition === loanSourceHandlePosition) {
		return null;
	}

	return {
		position: loanSourceHandlePosition,
		sourceLoanEdges,
	};
};

const makeEdge = (
	edge: EdgeRes,
	nodes: Node<NodeData>[],
	chartSettings: CapChartLocalStorageSettings
): Edge<EdgeRes> => {
	const isDebt = edge.type === 'loan';
	const label = makeEdgeLabel(edge, chartSettings);
	const type = isDebt ? EdgeTypes.DEBT : EdgeTypes.EQUITY;

	return {
		id: `${edge.fromNodeId}-${edge.toNodeId}-${type}`,
		source: edge.fromNodeId,
		target: edge.toNodeId,
		sourceHandle: isDebt ? 'source-debt' : 'source',
		targetHandle: makeTargetHandleId(isDebt, edge.fromNodeId, edge.toNodeId, nodes),
		type,
		label,
		data: edge,
		style: {
			stroke: '#d3d8da',
		},
		...(label
			? {
					labelShowBg: true,
					labelStyle: {
						fontSize: '14px',
						fill: '#4F616C',
						position: 'absolute',
						transform: `translate(0%, 1px)`,
					},
					labelBgStyle: {
						fill: '#F4F5F6',
						stroke: '#E9EBED',
					},
					labelBgPadding: [4, 2],
					labelBgBorderRadius: 2,
				}
			: {}),
	};
};

export const makeEdgeLabel = (data?: EdgeRes, settings?: CapChartLocalStorageSettings) => {
	const { showSummarized, valueDisplayMode, valuesHeldType } = settings || {};
	const showPercentages = valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE;
	const isLoan = data?.type === 'loan';
	return (
		formatCapValue({
			holderValues: {
				amount: data?.totalHeld || 0,
				amountPercent: data?.costPercent,
				votes: data?.votes,
				votesPercent: data?.votesPercent,
				totalCost: data?.cost || 0,
				totalCostPercent: data?.costPercent,
			},
			valuesHeldType: valuesHeldType || CAP_TABLE_VALUES_HELD_TYPE.VOTES,
			showSummarized: !!showSummarized,
			isLoan,
			showPercentages,
		}) || ''
	);
};

export const makeTargetHandleId = (
	isDebt: boolean,
	sourceNodeId: string,
	targetNodeId: string,
	nodes: Node<NodeData>[],
	sourcePosition?: Position
) => {
	if (!isDebt) {
		return 'target';
	}

	const edgeTargetNode = nodes.find((n) => n.id === targetNodeId);
	const edgeSourceNode = nodes.find(
		(n) => n.id === sourceNodeId && targetNodeId === edgeTargetNode?.id
	);
	const targetPosition = sourcePosition || edgeSourceNode?.data?.loanData?.sourcePosition;

	return `target-debt-${targetPosition}`;
};

const getLegalType = (legalForm?: LegalFormType | null): NodeTypes => {
	switch (legalForm) {
		case LegalFormType.PARTNERSHIP:
			return NodeTypes.PARTNERSHIP;
		case LegalFormType.COMPANY:
			return NodeTypes.COMPANY;
		case LegalFormType.TRUST:
			return NodeTypes.TRUST;
		default:
			return NodeTypes.OTHER;
	}
};

const isNodeEndAfterCenter = (
	nodeX: number,
	{ chartMinX, chartMaxX }: ChartXPosition,
	nodeWidth: number = NODE_WIDTH
): boolean => {
	return nodeX - nodeWidth / 2 > (chartMinX + chartMaxX) / 2;
};

const isDagreNodeEndAfterCenter = (
	nodeWithPosition: dagre.Node,
	graphGraph: dagre.graphlib.Graph
): boolean => {
	const graph = graphGraph.graph();
	const graphHorizontalCenter = (graph.width || 0) / 2;
	const nodeStartPosition = nodeWithPosition?.x;

	return nodeStartPosition > graphHorizontalCenter;
};
